import React, {Dispatch, ReactElement, SetStateAction, useState} from "react";
import {ProcessErrorType, ProcessType} from "@process/types/process.types";
import {Button, Form} from "react-bootstrap";
import {ShipmentMethodSelect} from "@shipment/components";
import {ShipmentMethodKeyEnum, ShipmentMethodType, ShipmentMethodTypeEnum} from "@shipment/types/shipmentMethod.types";
import {AddressForm, FormError} from "@src/components";
import {DepartmentSelect} from "@department/components";
import {Maybe} from "@src/types/global.types";
import {DepartmentType} from "@department/types/department.types";
import {AddressType} from "@src/types/address.types";


interface ProcessFormShipmentProps {
    process: ProcessType
    setProcess: Dispatch<SetStateAction<ProcessType>>
    formErrors: ProcessErrorType
    removeFromFormErrors: Function
}

const ProcessFormShipment: React.FC<ProcessFormShipmentProps> = ({
    process, setProcess, formErrors, removeFromFormErrors
}): ReactElement => {
    const onShipmentMethodChangeHandler = (shipmentMethod: Maybe<ShipmentMethodType>) => {
        removeFromFormErrors('shipment');
        setProcess((prevState: ProcessType) => ({
            ...prevState,
            hasDifferentShipmentDepartment: false,
            hasDifferentShipmentAddress: false,
            shipment: {...prevState.shipment, shipmentMethod}
        }));
    }

    const onShipmentDepartmentChangeHandler = (department: Maybe<DepartmentType>) => {
        setProcess(prevState => ({...prevState, shipment: {...prevState.shipment, department}}));
    }

    const setShipmentAddress = (address: Maybe<AddressType>) => {
        setProcess((prevState: ProcessType) => ({
            ...prevState, shipment: {...prevState.shipment, address}
        }));
    }

    const copyAddressFromCustomer = (e: React.MouseEvent<HTMLElement>) => {
        setProcess((prevState: ProcessType) => ({
            ...prevState, shipment: {...prevState.shipment, address: {...prevState.customer.address}}
        }));
    }

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name: string = e.target.name;
        const type: string = e.target.type;
        let value: string|boolean;

        if ('checkbox' === type) {
            value = e.target.checked;
        } else {
            value = e.target.value;
        }

        setProcess(prevState => ({...prevState, [name]: value}));
        removeFromFormErrors(name);
    }

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Metoda zwrotu do klienta</Form.Label>
                <ShipmentMethodSelect
                    type={ShipmentMethodTypeEnum.outgoing}
                    value={process?.shipment?.shipmentMethod}
                    onChange={onShipmentMethodChangeHandler}
                    className={formErrors?.shipment?.shipmentMethod ? ' is-invalid' : ''}
                />
                <FormError errors={formErrors?.shipment?.shipmentMethod} />
            </Form.Group>

            {process?.shipment?.shipmentMethod?.key === ShipmentMethodKeyEnum.personal && (
                <>
                    <Form.Group className="mb-3" controlId="hasDifferentShipmentDepartment">
                        <Form.Check
                            type="switch"
                            name="hasDifferentShipmentDepartment"
                            checked={process.hasDifferentShipmentDepartment}
                            label="Oddział wydania jest inny niż oddział, który obsługuje serwis."
                            onChange={onChangeHandler}
                        />
                    </Form.Group>

                    {process.hasDifferentShipmentDepartment && (
                        <div className="mb-3 p-2 pb-0 bg-light border">

                            <Form.Group className="mb-3">
                                <Form.Label>Oddział wydania</Form.Label>
                                <DepartmentSelect
                                    onChange={onShipmentDepartmentChangeHandler}
                                    value={process?.shipment?.department}
                                    isInvalid={!!formErrors?.shipment?.department}
                                />
                                <FormError errors={formErrors?.shipment?.department} />
                            </Form.Group>
                        </div>
                    )}
                </>
            )}

            {(   process?.shipment?.shipmentMethod?.key === ShipmentMethodKeyEnum.courier
                || process?.shipment?.shipmentMethod?.key === ShipmentMethodKeyEnum.mobile
            ) && (
                <>
                    <Form.Group className="mb-3" controlId="hasDifferentShipmentAddress">
                        <Form.Check
                            type="switch"
                            name="hasDifferentShipmentAddress"
                            checked={process.hasDifferentShipmentAddress}
                            label="Adres dostawy jest inny niż adres klienta."
                            onChange={onChangeHandler}
                        />
                    </Form.Group>

                    {process.hasDifferentShipmentAddress && (
                        <div className="mb-3 p-2 pb-0 bg-light border">
                            <div className="d-flex gap-3">
                                <legend className="fs-3">Adres dostarczenia do klienta</legend>
                                <div>
                                    <Button onClick={copyAddressFromCustomer}
                                            variant={"outline-azure"} size={"sm"}
                                    >skopiuj z danych klienta</Button>
                                </div>
                            </div>
                            <AddressForm
                                address={process?.shipment?.address}
                                formErrors={formErrors?.shipment?.address}
                                setAddress={setShipmentAddress}
                            />
                        </div>

                    )}
                </>
            )}
        </>
    );
}

export default ProcessFormShipment;